import PropTypes from 'prop-types';
import React, { memo, useEffect } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';

import { get, getRulesFromState, applyVerticalStyles, pageContext, getQueryVariable } from 'ctm-util-functions';
import { useBrandContentFactory } from 'ctm-app/client/hooks/useBrandContentFactory';
import { SimpleFlexbox } from '../flexbox/flexbox';
import { LoadingSpinner } from '../loading_animation/loading_spinner';

import { BrandLogo } from '../brand_logo/brand_logo';
import { TextTemplate } from '../text_template/text_template';
import styles from './please_wait_overlay.css';
import Sergei from '../../../../build/assets/images/logos/car-insurance/sergei.png';
import { ErrorPageQuote } from '../error_page/error_quote';

/**
 * Get logo component(s) for the loading page to render
 * @param {Array<String>} brandLogo.brands - brands
 * @param {String} brandLogo.srcPath - src path
 * @param {Object<String>} verticalStyles - styles
 * @returns {Array<BrandLogo>} List of BrandLogo components
 */
const getLogos = ({ brands = [], srcPath = '' }, verticalStyles) => brands.map((brandCode) => <BrandLogo key={`logo_${brandCode}`} className={verticalStyles.brandLogo} srcPath={srcPath} brandCode={brandCode} />);
const displayText = (isCarOrHome, stateKey, verticalStyles, subLabelFirstLineHeading, subLabelFirstLine, subLabelSecondLine) => {
    return isCarOrHome ? (
        <div stateKey={stateKey} tagName="h4" className={classNames('test-savingsTip', verticalStyles.subLabelLine, verticalStyles.savingsTip)}>
            {subLabelFirstLine && (
                <div className={verticalStyles.titleContent}>
                    <strong>
                        {subLabelFirstLineHeading || 'Savings tip'}
                        :&nbsp;
                    </strong>
                </div>
            )}
            <div className={verticalStyles.bodyContent}>
                <div>{subLabelFirstLine}</div>
                <div>{subLabelSecondLine}</div>
            </div>
        </div>
    ) : (
        ''
    );
};

const rpoHandover = (offers, url, verticalStyles) => {
    return (
        <div className={verticalStyles.container}>
            <img src={Sergei} alt="Sergei banner" />
            <h1>Thanks for comparing with us.</h1>
            <p className={verticalStyles.mainText}>
                Once you have completed your purchase,
                {' '}
                you will receive an email inviting you to claim your e-gift card
                {' '}
                <b>30 days after the start date of your policy.</b>
            </p>
            <a className={verticalStyles.termsConditions} href={offers[0].offer.termsAndConditionsUrl} target="_blank" rel="noreferrer">
                Terms and conditions apply
            </a>
            <div className={verticalStyles.divider} />
            <p className={verticalStyles.greyText}>
                We are now passing your details and product choice to our trusted provider. If you are not redirected within 10 seconds,
                {' '}
                <a className={verticalStyles.handoverUrl} href={url}>
                    please click here.
                </a>
            </p>
        </div>
    );
};

/**
 * Renders the layout of the please wait page plus and its children: text, loading spinner, and logos (if any)
 * @returns {Node|null} - if visible, renders the page, otherwise nothing
 */
const _PleaseWaitOverlay = ({
    visible,
    brandLogo,
    label,
    subLabelFirstLineHeading,
    subLabelFirstLine,
    subLabelSecondLine,
    stateKey,
    variant,
    children,
    verticalStyles,
    contentProps,
    isCarOrHome,
    offerDetails,
    handoverUrl,
    pageState,
    updatePleaseWait
}) => {
    useEffect(() => {
        if (pageState.lastViewedPage === 'confirmation' && getQueryVariable('isEdit')) {
            updatePleaseWait(true);
        }
    }, [pageState.lastViewedPage]);
    const brandContentFactory = useBrandContentFactory();
    const loadingSpinnerType = brandContentFactory.spinnerType();
    const isXs = pageContext.getProps().pageState.breakpoint === 'xs';
    const errorPageProps = {
        isXs,
        href: 'car-insurance/journey/start'
    };

    const renderDisplayErrorComponent = () => {
        return <ErrorPageQuote {...errorPageProps} />;
    };

    const renderConfirmationLoader = () => {
        setTimeout(() => {
            updatePleaseWait(false);
        }, 2000);
        return (
            <>
                {isXs ? (
                    <div>
                        <TextTemplate label={"This won't take long..."} stateKey={stateKey} tagName="h2" className={verticalStyles.label} />
                        <p className={verticalStyles.hint}>Did you know?</p>
                        <span className={verticalStyles.spanContainer}>
                            <p className={verticalStyles.hintSubText}>Some insurers offer a discount if you purchase online, but this is normally only for the first 12 months.</p>
                        </span>
                        <LoadingSpinner type={loadingSpinnerType} />
                    </div>
                ) : (
                    <div>
                        <TextTemplate label={"This won't take long..."} stateKey={stateKey} tagName="h2" className={verticalStyles.label} />
                        <span className={verticalStyles.spanContainer}>
                            <p className={verticalStyles.hint}>Did you know?</p>
                            <p className={verticalStyles.hintSubText}>
                                Some insurers offer a discount if you purchase online, but this
                                <br />
                            </p>
                        </span>
                        <p className={verticalStyles.hintSubText}>is normally only for the first 12 months.</p>
                        <LoadingSpinner type={loadingSpinnerType} />
                    </div>
                )}
            </>
        );
    };

    const renderBaseComponent = () => {
        if (offerDetails && offerDetails.length > 0 && window.location.pathname === '/car-insurance/journey/handover') {
            return rpoHandover(offerDetails, handoverUrl, verticalStyles);
        }

        // If user lands on verification page, do not show the please wait loader
        if (window.location.pathname === '/car-insurance/journey/verify') {
            updatePleaseWait(false);
        }

        // Check if a renderBrandLogos is set to true
        // If so, only then should we try and render brand logos
        const logos = brandLogo?.renderBrandLogos && brandLogo?.srcPath && getLogos({ brands: brandContentFactory.partnerBrands(), srcPath: brandLogo.srcPath }, verticalStyles);
        return (
            <>
                <TextTemplate label={label} stateKey={stateKey} tagName="h2" className={verticalStyles.label} />
                <div stateKey={stateKey} tagName="h4" className={verticalStyles.subLabelLine}>
                    {displayText(isCarOrHome, stateKey, verticalStyles, subLabelFirstLineHeading, subLabelFirstLine, subLabelSecondLine)}
                    {subLabelFirstLine && !isCarOrHome && <TextTemplate label={subLabelFirstLine} stateKey={stateKey} tagName="h4" className={verticalStyles.subLabelLine} />}
                    {subLabelSecondLine && !isCarOrHome && <TextTemplate label={subLabelSecondLine} stateKey={stateKey} tagName="h4" className={verticalStyles.subLabelLine} />}
                </div>
                <LoadingSpinner type={loadingSpinnerType} />
                <SimpleFlexbox className={verticalStyles[`variant_${variant}`]} row wrap contentCenter="xs">
                    {logos}
                </SimpleFlexbox>
                {children}
            </>
        );
    };

    const displayComponent = () => {
        if (pageState.displayErrorPageWithNewQuoteButton === true) {
            return renderDisplayErrorComponent();
        }
        if (pageState.lastViewedPage === 'confirmation' && getQueryVariable('isEdit') && pageState.displayErrorPageWithNewQuoteButton === false) {
            return renderConfirmationLoader();
        }
        return renderBaseComponent();
    };

    return (
        visible && (
            <SimpleFlexbox containerFluid>
                <SimpleFlexbox container display="flex" contentCenter="xs">
                    <SimpleFlexbox className={verticalStyles.main} column display="flex" itemsCenter="xs">
                        {displayComponent()}
                    </SimpleFlexbox>
                </SimpleFlexbox>
            </SimpleFlexbox>
        )
    );
};
const mapStateToProps = (state, props) => {
    const { contentProps, visualProps, variant, label, subLabelFirstLineHeading, subLabelFirstLine, subLabelSecondLine, stateKey, brandLogo } = props;
    const visible = get(getRulesFromState(state, props), 'visibility.result');
    const verticalStyles = applyVerticalStyles(styles, 'pleaseWaitOverlay');
    const isCarOrHome = state.getIn(['page', 'vertical']) === 'home-contents-insurance' || state.getIn(['page', 'vertical']) === 'car-insurance';
    const offerDetails = state.getIn(['user', 'journey', 'offers']);
    const handoverUrl = state.getIn(['vertical', 'quote', 'results', 'productInfo', 'product', 'quoteUrl']);
    const pageState = state.getIn(['page']);

    return {
        verticalStyles,
        visible,
        label: contentProps.label ? contentProps.label : label,
        subLabelFirstLineHeading: contentProps.subLabelFirstLineHeading ? contentProps.subLabelFirstLineHeading : subLabelFirstLineHeading,
        subLabelFirstLine: contentProps.subLabelFirstLine ? contentProps.subLabelFirstLine : subLabelFirstLine,
        subLabelSecondLine: contentProps.subLabelSecondLine ? contentProps.subLabelSecondLine : subLabelSecondLine,
        stateKey: contentProps.stateKey ? contentProps.stateKey : stateKey,
        brandLogo: contentProps.brandLogo ? contentProps.brandLogo : brandLogo,
        variant: visualProps.variant ? visualProps.variant : variant,
        isCarOrHome,
        offerDetails,
        handoverUrl,
        pageState: JSON.parse(JSON.stringify(pageState))
    };
};

_PleaseWaitOverlay.propTypes = {
    contentProps: PropTypes.shape({
        label: PropTypes.string.isRequired, // Text to display on the loading page, can be a template
        subLabelFirstLineHeading: PropTypes.string,
        subLabelFirstLine: PropTypes.string,
        subLabelSecondLine: PropTypes.string,
        stateKey: PropTypes.string, // state tree path, if supplied, along with logoCodeStatePath, will be used to determine the path to the logo image
        spinnerType: PropTypes.string,
        brandLogo: PropTypes.shape({
            srcPath: PropTypes.string, // logo image assets url, vertical specific. e.g /evt/assets/images/logos/home-contents-insurance
            brands: PropTypes.arrayOf(PropTypes.string) // logo code array, if supplied, the component will use this instead of logoCodeStatePath
        })
    }),
    visualProps: PropTypes.shape({
        variant: PropTypes.string
    }),
    brandLogo: PropTypes.shape({
        srcPath: PropTypes.string, // logo image assets url, vertical specific. e.g /evt/assets/images/logos/home-contents-insurance
        brands: PropTypes.arrayOf(PropTypes.string) // logo code array, if supplied, the component will use this instead of logoCodeStatePath
    }),
    children: PropTypes.node,
    visible: PropTypes.bool,
    label: PropTypes.string,
    subLabelFirstLineHeading: PropTypes.string,
    subLabelFirstLine: PropTypes.string,
    subLabelSecondLine: PropTypes.string,
    stateKey: PropTypes.string,
    variant: PropTypes.string,
    verticalStyles: PropTypes.objectOf(PropTypes.string),
    isCarOrHome: PropTypes.bool
};

_PleaseWaitOverlay.defaultProps = {
    brandLogo: {},
    contentProps: {},
    visualProps: {},
    stateKey: 'vertical.quote',
    verticalStyles: {}
};

const mapDispatchToProps = (dispatch) => ({
    updatePleaseWait: (value) => {
        dispatch({
            type: 'SET_PAGE_VALUE',
            identifier: 'showPleaseWait',
            value
        });
    }
});

const PleaseWaitOverlay = memo(
    connect(
        mapStateToProps,
        mapDispatchToProps
    )(_PleaseWaitOverlay)
);

export { _PleaseWaitOverlay, PleaseWaitOverlay };
